/**
 * Per-unit DOM rich-overlay: level badge + name tag + buff/debuff strip
 * + INCAPACITATED big countdown. Worldspace-projected each frame via
 * Vector3.project() (NOT CSS2DRenderer — manual projection lets us only
 * update the small "rich set" instead of every registered element).
 *
 * The manager (manager.ts) decides which units get a rich overlay; this
 * module just builds and updates one DOM root.
 *
 * Visibility: off-screen / behind-camera projects to NDC outside [-1,1]
 * → display:none. On-screen rich elements update transform per frame.
 *
 * Spec: docs/superpowers/specs/2026-05-03-floating-overhead-ui-design.md §"Architecture" Layer B
 */
import * as THREE from "three";
import type { BehaviorPresentation } from "./registry";
import { lookupBehavior } from "./registry";

export type TeamRelation = "ally" | "enemy" | "neutral";

export interface RichOverlay {
  el: HTMLDivElement;
  /**
   * Update content. Called by manager per frame.
   *  level / name → header
   *  behaviorIds + behaviorExpiresAt → buff strip + big-countdown
   */
  update(args: {
    name: string;
    level: number;
    relation: TeamRelation;
    behaviors: Map<string, number>;  // id → expiresAt (0 = permanent)
    now: number;
  }): void;
  /**
   * Project worldPos → screen and translate the overlay. Off-screen → display:none.
   */
  project(worldPos: THREE.Vector3, camera: THREE.Camera): void;
  /** Manual hide (e.g., when entity dies). */
  setVisible(v: boolean): void;
  /** Remove from DOM + drop refs. */
  destroy(): void;
}

const tmpVec = new THREE.Vector3();

export function createRichOverlay(parent: HTMLElement): RichOverlay {
  const el = document.createElement("div");
  el.className = "fl-root";
  el.innerHTML = `
    <div class="fl-big-countdown" style="display:none;">
      <div class="fl-bc-label"></div>
      <div class="fl-bc-timer"></div>
    </div>
    <div class="fl-name-tag"></div>
    <div class="fl-strip-row">
      <div class="fl-buff-strip"></div>
    </div>
  `;
  el.style.display = "none";
  parent.appendChild(el);

  const bigCountdown = el.querySelector<HTMLDivElement>(".fl-big-countdown")!;
  const bcLabel = el.querySelector<HTMLDivElement>(".fl-bc-label")!;
  const bcTimer = el.querySelector<HTMLDivElement>(".fl-bc-timer")!;
  const nameTag = el.querySelector<HTMLDivElement>(".fl-name-tag")!;
  const stripRow = el.querySelector<HTMLDivElement>(".fl-strip-row")!;
  const buffStrip = el.querySelector<HTMLDivElement>(".fl-buff-strip")!;

  // Track currently-rendered buff icons so we can diff against incoming
  // behaviors and add/remove minimally (avoids re-creating identical DOM).
  const activeIcons = new Map<string, HTMLDivElement>();

  let onScreen = false;

  function update(args: {
    name: string;
    level: number;
    relation: TeamRelation;
    behaviors: Map<string, number>;
    now: number;
  }): void {
    nameTag.textContent = args.name || "";
    el.dataset.relation = args.relation;

    // Big-countdown: pick the first behavior whose presentation is big-countdown
    let bigBehavior: { id: string; presentation: BehaviorPresentation; expiresAt: number } | null = null;
    const stripIds: string[] = [];
    args.behaviors.forEach((expiresAt, id) => {
      const p = lookupBehavior(id);
      if (!p || p.hidden) return;
      if (p.template === "big-countdown") {
        if (!bigBehavior) bigBehavior = { id, presentation: p, expiresAt };
      } else {
        stripIds.push(id);
      }
    });

    if (bigBehavior !== null) {
      const bb = bigBehavior as { id: string; presentation: BehaviorPresentation; expiresAt: number };
      bigCountdown.style.display = "block";
      bcLabel.textContent = bb.presentation.label ?? "";
      const remainingMs = bb.expiresAt > 0 ? Math.max(0, bb.expiresAt - args.now) : 0;
      const remainingSec = Math.ceil(remainingMs / 1000);
      bcTimer.textContent = bb.expiresAt > 0 ? String(remainingSec) : "";
      bigCountdown.dataset.glow = bb.presentation.glowColor;
      bigCountdown.classList.toggle("fl-pulse", bb.presentation.pulse);
      // While big-countdown is up, hide the strip row (visual tradeoff —
      // INCAPACITATED is the focal element)
      stripRow.style.display = "none";
    } else {
      bigCountdown.style.display = "none";
      stripRow.style.display = "flex";
    }

    // Diff buff strip — add new icons, remove gone ones
    const targetIds = new Set(stripIds);
    for (const [id, iconEl] of activeIcons) {
      if (!targetIds.has(id)) {
        iconEl.remove();
        activeIcons.delete(id);
      }
    }
    for (const id of stripIds) {
      if (activeIcons.has(id)) continue;
      const p = lookupBehavior(id)!;
      const icon = document.createElement("div");
      icon.className = "fl-buff-icon";
      icon.dataset.glow = p.glowColor;
      if (p.pulse) icon.classList.add("fl-pulse");
      icon.innerHTML = p.icon;
      // Hover tooltip: "Name — effect" when the registry provides them, else the raw id.
      icon.title = p.desc ? `${p.name ?? id} — ${p.desc}` : (p.name ?? id);
      buffStrip.appendChild(icon);
      activeIcons.set(id, icon);
    }
  }

  function project(worldPos: THREE.Vector3, camera: THREE.Camera): void {
    tmpVec.copy(worldPos).project(camera);
    // Cull behind camera or outside NDC viewport (with small grace)
    const visible =
      tmpVec.z >= -1 && tmpVec.z <= 1 &&
      tmpVec.x >= -1.2 && tmpVec.x <= 1.2 &&
      tmpVec.y >= -1.2 && tmpVec.y <= 1.2;
    if (!visible) {
      if (onScreen) {
        el.style.display = "none";
        onScreen = false;
      }
      return;
    }
    if (!onScreen) {
      el.style.display = "block";
      onScreen = true;
    }
    const sx = (tmpVec.x * 0.5 + 0.5) * window.innerWidth;
    const sy = (-tmpVec.y * 0.5 + 0.5) * window.innerHeight;
    el.style.transform = `translate3d(${Math.round(sx)}px, ${Math.round(sy)}px, 0) translate(-50%, -100%)`;
  }

  function setVisible(v: boolean): void {
    el.style.display = v ? "block" : "none";
    onScreen = v;
  }

  function destroy(): void {
    el.remove();
    activeIcons.clear();
  }

  return { el, update, project, setVisible, destroy };
}
